<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="returnData"
        :options.sync="options"
        item-key="id"
        :search="search"
        :footer-props="footerProps"
        :loading-text="$t('return.returnsLoading')"
        :no-data-text="$t('return.noReturns')"
        :mobile-breakpoint="mobileBreakpoint"
        :loading="loadingData"
        :server-items-length="totalReturns"
        dense
        :header-props="headerProps"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-btn
              :disabled="!isCustomerService && !canCreateReturn"
              color="primary"
              outlined
              :to="'/returnSearchOrder'"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <v-badge overlap :value="nbActiveFilters" :content="nbActiveFilters">
              <v-btn color="primary" outlined class="ml-2" @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-spacer></v-spacer>
            <v-btn
              class="mr-2"
              :disabled="loadingData || totalReturns == 0"
              color="primary"
              outlined
              @click="showChooseFieldToExport = true"
            >
              <v-icon>mdi-file-delimited</v-icon>
            </v-btn>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>

        <template v-slot:item.origin_location_id="{ item }">
          <router-link
            v-if="item.origin_location_id && canAccessStore(item.origin_location_id)"
            :to="{ name: 'locationEdit', params: { id: item.origin_location_id } }"
            class="text-decoration-none d-block text-left px-0"
          >
            {{ item.origin_location_id }}
          </router-link>
          <v-card-text v-else-if="item.origin_location_id" class="text-left pl-0 py-0 text--primary">
            {{ item.origin_location_id }}
          </v-card-text>
          <v-card-text v-else class="text-left py-0 text--secondary pl-0 py-0">X</v-card-text>
        </template>

        <template v-slot:item.total_nb_product="{ item }">
          {{ countProducts(item.items) }}
        </template>

        <template v-slot:item.amount="{ item }">
          <span v-if="item.refunded_amount !== undefined">{{ item.refunded_amount | formatPrice(item.currency) }}</span>
          <span v-else>{{ item.amount | formatPrice(item.currency) }}</span>
          {{ item.currency ? item.currency.code : item.currency_code }}
        </template>

        <template v-slot:item.current_status="{ item }">
          {{ getStatusTranslation(item.current_status) }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn
            :class="[$vuetify.breakpoint.width >= 768 ? '' : 'pr-0']"
            text
            color="primary"
            @click="executeAction(item.return_id)"
          >
            <v-icon v-if="$vuetify.breakpoint.width > 767 && $vuetify.breakpoint.width < 1264" color="primary">
              mdi-magnify
            </v-icon>
            <span v-else>{{ $t('return.detail') }}</span>
          </v-btn>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :title="$t('return.filterReturns')"
        :fields="headers"
        @confirm="filter"
        :loadingData="loadingData"
      ></filter-dialog>
      <export-fields-dialog
        :fields="csvHeaderExport"
        :itemsFields="csvItemsExport"
        v-model="showChooseFieldToExport"
        :title="$t('exportCsv.csvName')"
        @exportCSV="exportCSV"
      ></export-fields-dialog>
    </v-card>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import { mapState } from 'vuex'
  import exportFieldsDialog from '@/components/SubOrders/exportFieldsDialog.vue'
  import { ReturnSettingsService } from '@/services/returnSettingsService.js'

  export default {
    name: 'ReturnTable',
    components: {
      exportFieldsDialog,
    },
    data() {
      return {
        search: '',
        returnData: [],
        options: {
          itemsPerPage: 15,
          sortDesc: [],
        },
        loadingData: false,
        totalReturns: 0,
        filters: [],
        displayFilters: false,
        showChooseFieldToExport: false,
        allowReturnCreationInShop: [],
        displayFieldSettings: null,
        formFiltersKey: Date.now(),
        mobileBreakpoint: 768,
        returnSettingsService: null,
      }
    },
    created() {
      this.returnSettingsService = new ReturnSettingsService(this.$auth)
    },
    mounted() {
      let countryCode
      if (this.currentStoreDetails && this.currentStoreDetails.address && this.currentStoreDetails.address.country_id) {
        countryCode = this.currentStoreDetails.address.country_id
      }
      this.getReturnSettings(countryCode)
      this.getSettingsByCountry('FRA')
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset current page number and delivery methods per page if the previous page wasn't the details of a delivery method
      if (previousPath !== 'returns') {
        this.filters = []
        this.formFiltersKey = Date.now()
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
      }
      this.returnData = []
      this.list()
    },
    methods: {
      executeAction: function (orderId) {
        this.$router.push('/returns/' + orderId)
      },
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.options.page = 1
        this.list()
      },
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      list() {
        this.loadingData = true
        let myInit = this.buildMyInit(true)
        this.$Amplify.API.get('returns', '/v1/int/returns', myInit)
          .then((res) => {
            this.returnData = res.data
            this.totalReturns = res.nbReturns
            this.loadingData = false
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('return.returnsLoadingError'),
              color: 'error',
            })
          })
      },
      countProducts(items) {
        let nbItems = 0
        for (const item of items) {
          if (item.return_quantity) {
            nbItems += item.return_quantity
          }
        }
        return nbItems
      },
      getStatusTranslation: function (statusValue) {
        var status = this.status.find((status) => status.value == statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      exportCSV(value) {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('order.CSVGenerationInProgress'),
          color: 'success',
        })
        /* get order items without pagination */
        this.loadingData = true
        let myInit = this.buildMyInit(false)
        myInit.queryStringParameters.csvHeaders = JSON.stringify(value.csvHeadersToExport)
        myInit.queryStringParameters.exportItemsLine = value.exportItemsLine
        myInit.queryStringParameters.exportCustomFields = value.exportCustomFields
        this.$Amplify.API.get('returns', '/v1/int/returns/export', myInit)
          .then((res) => {
            if (res.job_id) {
              this.pollJob = setInterval(() => {
                this.$Amplify.API.get('returns', '/v1/int/jobs/' + res.job_id, {
                  headers: { Authorization: 'Bearer ' + this.$auth.accessToken },
                })
                  .then((res) => {
                    let url = res.file_archive_url
                    if (url) {
                      //we create an <a> element and we simulate a click on it to trigger the downloading
                      const link = document.createElement('a')
                      link.href = url
                      document.body.appendChild(link)
                      link.click()
                      clearInterval(this.pollJob)
                      this.loadingData = false
                    } else if (res.status == 'error') {
                      console.log(res.error_message)
                      this.loadingData = false
                      clearInterval(this.pollJob)
                      EventBus.$emit('displaySnackbar', {
                        display: true,
                        text: this.$t('subOrder.jobLoadingErrorMessage'),
                        color: 'error',
                      })
                    }
                  })
                  .catch((e) => {
                    console.log(e)
                    clearInterval(this.pollJob)
                    this.loadingData = false
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: this.$t('subOrder.jobLoadingErrorMessage'),
                      color: 'error',
                    })
                  })
              }, 5000)
            } else {
              this.loadingData = false
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('subOrder.jobLoadingErrorMessage'),
                color: 'error',
              })
            }
          })
          .catch((e) => {
            console.log(e)
            this.loadingData = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.jobCreationErrorMessage'),
              color: 'error',
            })
          })
      },
      buildMyInit: function (pagination = true) {
        var { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
          },
        }
        if (pagination) {
          myInit.queryStringParameters.page = page
          myInit.queryStringParameters.itemsPerPage = itemsPerPage
        } else {
          myInit.queryStringParameters.itemsPerPage = false
        }

        let filters = {}
        for (let filter of this.filters) {
          if (filter.active) {
            if (filter.name === 'type') {
              myInit.queryStringParameters.types = filter.value
            } else if (filter.name === 'current_status') {
              myInit.queryStringParameters.status = filter.value
            } else {
              filters[filter.name] = filter.value
            }
          }
        }

        if (Object.entries(filters).length !== 0) {
          myInit.queryStringParameters.filters = JSON.stringify(filters)
        }

        return myInit
      },
      async getReturnSettings(countryCode) {
        try {
          const settings = await this.returnSettingsService.getReturnSettings(countryCode)
          this.allowReturnCreationInShop =
            settings && settings.allow_return_creation_in_shop
              ? settings.allow_return_creation_in_shop
              : ['oms-shop-manager']
        } catch (e) {
          console.error(e)
          this.allowReturnCreationInShop = ['oms-shop-manager']
        }
      },
      getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          .then((res) => {
            this.displayFieldSettings = res.display?.customer_information
          })
          .catch((e) => {
            console.log(e)
          })
      },
      canAccessStore(locationId) {
        let isCurrentStoreManagedByUser = this.isShopManager && this.currentStore === locationId
        return this.isCustomerService || isCurrentStoreManagedByUser
      },
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'roleRights', 'roles', 'currentStoreDetails', 'isShopManager']),

      canCreateReturn() {
        // Check if user has any role that's in the allowReturnCreationInShop array
        if (!this.allowReturnCreationInShop || this.allowReturnCreationInShop.length === 0) {
          return false
        }
        // Check if any user role matches allowed roles
        return this.roles.some((role) => this.allowReturnCreationInShop.includes(role))
      },
      nbActiveFilters() {
        return this.filters.filter((filter) => filter.active).length
      },
      status() {
        return [
          { text: this.$t('return.waiting'), value: 'waiting' },
          { text: this.$t('return.waitingProductsReceipt'), value: 'waiting_products_receipt' },
          { text: this.$t('return.productsReceived'), value: 'products_received' },
          { text: this.$t('return.productsPartiallyReceived'), value: 'products_partially_received' },
          { text: this.$t('return.finished'), value: 'finished' },
          { text: this.$t('return.cancelled'), value: 'cancelled' },
        ]
      },
      headers() {
        return [
          {
            text: this.$t('return.reference'),
            value: 'return_id',
            filterType: 'text',
          },
          {
            text: this.$t('return.numOrder'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('return.date'),
            value: 'date',
            filterType: 'date',
          },
          {
            text: this.$t('return.location'),
            value: 'origin_location_id',
          },

          {
            text: this.$t('return.products'),
            value: 'total_nb_product',
            sortable: false,
          },
          {
            text: this.$t('return.amount'),
            value: 'amount',
            filterType: 'range',
            sortable: false,
          },
          {
            text: this.$t('return.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.status,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('return.returnsPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('return.sortByText'),
        }
      },
      csvHeaderExport() {
        return { text: this.$t('exportCsv.fieldsToExport'), arrayValues: this.csvHeaders }
      },
      csvItemsExport() {
        return { text: this.$t('exportCsv.itemFieldsToExport'), arrayValues: this.itemsFields }
      },
      csvHeaders() {
        let headers = [
          { text: this.$t('exportCsv.numOrder'), value: 'order_id' },
          { text: this.$t('exportCsv.returnId'), value: 'return_id' },
          { text: this.$t('exportCsv.date'), value: 'date' },
        ]
        if (this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])) {
          headers.push({ text: this.$t('exportCsv.customer'), value: 'customer' })
        }
        headers.push(
          { text: this.$t('exportCsv.currentStatus'), value: 'current_status' },
          { text: this.$t('exportCsv.products'), value: 'products' },
          { text: this.$t('exportCsv.currencyCode'), value: 'currency_code' },
          { text: this.$t('exportCsv.updatedAt'), value: 'updated_at' },
          { text: this.$t('exportCsv.amount'), value: 'amount' },
          { text: this.$t('exportCsv.returnedProducts'), value: 'returned_products' },
          { text: this.$t('exportCsv.refundDate'), value: 'refund_date' },
          { text: this.$t('exportCsv.refundReference'), value: 'refund_reference' },
          { text: this.$t('exportCsv.refundedAmount'), value: 'refunded_amount' },
          { text: this.$t('exportCsv.autoRefund'), value: 'auto_refund' },
          { text: this.$t('exportCsv.returnFeesAmount'), value: 'return_fees_amount' },
          { text: this.$t('exportCsv.isExternal'), value: 'is_external' }
        )
        return headers
      },
      itemsFields() {
        return [
          { text: this.$t('exportCsv.productId'), value: 'product_id' },
          { text: this.$t('exportCsv.productSalePrice'), value: 'product_sale_price' },
          { text: this.$t('exportCsv.productSalePriceExcludeTax'), value: 'product_sale_price_exclude_tax' },
          { text: this.$t('exportCsv.productDefaultPrice'), value: 'product_default_price' },
          { text: this.$t('exportCsv.productDefaultPriceExcludeTax'), value: 'product_default_price_exclude_tax' },
          { text: this.$t('exportCsv.barcode'), value: 'barcode' },
          { text: this.$t('exportCsv.productSku'), value: 'product_sku' },
          { text: this.$t('exportCsv.productTitle'), value: 'product_title' },
          { text: this.$t('exportCsv.returnReason'), value: 'return_reason' },
          { text: this.$t('exportCsv.nonRefundReason'), value: 'non_refund_reason' },
          { text: this.$t('exportCsv.refundedQuantity'), value: 'refunded_quantity' },
          { text: this.$t('exportCsv.returnedQuantity'), value: 'returned_quantity' },
          { text: this.$t('exportCsv.quantity'), value: 'return_quantity' },
          { text: this.$t('exportCsv.autoRefundLine'), value: 'auto_refund_line' },
        ]
      },
      isMobile() {
        return this.$vuetify.breakpoint.width < this.mobileBreakpoint
      },
    },
  }
</script>

<style></style>
